/**
 * Standalone Data Provider
 * 
 * Implements the DataProvider interface using browser-native APIs:
 * - IndexedDB for data persistence
 * - BroadcastChannel for cross-tab sync
 * - localStorage for settings and auth state
 * 
 * This provider works completely offline without any server connection.
 * 
 * 📖 Adding a new table? Read: docs/data-provider-guide.md
 *    1. Add to AmbiDBSchema interface below
 *    2. Add to TABLES in ./types.ts
 *    3. Increment DB_VERSION
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb'
import { logger } from '@/lib/logger'
import type {
  DataProvider,
  QueryOptions,
  QueryFilter,
  ChangePayload,
  Subscription,
  FileMetadata,
  AuthState,
  OperationResult,
  StandaloneUser
} from './types'
import { TABLES, STORAGE_BUCKETS } from './types'

// IndexedDB Schema
interface AmbiDBSchema extends DBSchema {
  // Data tables - all use string keys (UUIDs)
  scenes: { key: string; value: any; indexes: { 'by-user': string } }
  displays: { key: string; value: any; indexes: { 'by-user': string } }
  display_layers: { key: string; value: any; indexes: { 'by-config': string } }
  display_layer_overrides: { key: string; value: any; indexes: { 'by-config': string } }
  scene_display_configs: { key: string; value: any; indexes: { 'by-scene': string; 'by-display': string } }
  image_library: { key: string; value: any; indexes: { 'by-user': string; 'by-folder': string } }
  image_library_folders: { key: string; value: any; indexes: { 'by-user': string } }
  audio_library: { key: string; value: any; indexes: { 'by-user': string; 'by-folder': string } }
  audio_library_folders: { key: string; value: any; indexes: { 'by-user': string } }
  audio_library_files: { key: string; value: any; indexes: { 'by-user': string; 'by-folder': string } }
  music_nodes: { key: string; value: any; indexes: { 'by-user': string } }
  spotify_nodes: { key: string; value: any; indexes: { 'by-user': string } }
  playlist_nodes: { key: string; value: any; indexes: { 'by-user': string } }
  scene_music_configs: { key: string; value: any; indexes: { 'by-scene': string } }
  scene_spotify_configs: { key: string; value: any; indexes: { 'by-scene': string } }
  scene_playlist_configs: { key: string; value: any; indexes: { 'by-scene': string } }
  scene_light_configs: { key: string; value: any; indexes: { 'by-scene': string; 'by-light': string } }
  hue_bridges: { key: string; value: any; indexes: { 'by-user': string } }
  lights: { key: string; value: any; indexes: { 'by-bridge': string } }
  wled_devices: { key: string; value: any; indexes: { 'by-user': string; 'by-mac': string } }
  wled_outputs: { key: string; value: any; indexes: { 'by-device': string } }
  graph_containers: { key: string; value: any; indexes: { 'by-user': string } }
  spotify_integrations: { key: string; value: any; indexes: { 'by-user': string } }
  hue_sync_sessions: { key: string; value: any; indexes: { 'by-user': string } }
  characters: { key: string; value: any; indexes: { 'by-user': string; 'by-room': string } }
  game_rooms: { key: string; value: any; indexes: { 'by-gm': string; 'by-code': string } }
  
  // File storage - blobs stored with path as key
  'storage-user-images': { key: string; value: { blob: Blob; metadata: FileMetadata } }
  'storage-user-audio': { key: string; value: { blob: Blob; metadata: FileMetadata } }
  'storage-audio-files': { key: string; value: { blob: Blob; metadata: FileMetadata } }
}

const DB_NAME = 'ambi-standalone'
const DB_VERSION = 8  // Incremented to add wled_outputs table

// Standalone user storage key
const STANDALONE_USER_KEY = 'ambi-standalone-user'

export class StandaloneProvider implements DataProvider {
  private db: IDBPDatabase<AmbiDBSchema> | null = null
  private initialized = false
  private broadcastChannels: Map<string, BroadcastChannel> = new Map()
  private authStateCallbacks: Set<(state: AuthState) => void> = new Set()
  private currentUser: StandaloneUser | null = null
  
  // Object URL cache for file storage
  private objectUrlCache: Map<string, string> = new Map()

  // ==================== CRUD Operations ====================

  async query<T>(table: string, options?: QueryOptions): Promise<T[]> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const tx = this.db!.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    
    let results = await store.getAll()
    
    // Apply soft delete filter (exclude deleted records unless explicitly requested)
    if (!options?.includeDeleted) {
      results = results.filter(item => !(item as any).deleted_at)
    }
    
    // Apply filters
    if (options?.filters) {
      results = results.filter(item => this.matchesFilters(item, options.filters!))
    }
    
    // Apply ordering
    if (options?.orderBy && options.orderBy.length > 0) {
      results.sort((a, b) => {
        for (const order of options.orderBy!) {
          const aVal = a[order.column]
          const bVal = b[order.column]
          const comparison = this.compareValues(aVal, bVal)
          if (comparison !== 0) {
            return order.ascending === false ? -comparison : comparison
          }
        }
        return 0
      })
    }
    
    // Apply pagination
    if (options?.offset || options?.limit) {
      const start = options.offset || 0
      const end = options.limit ? start + options.limit : undefined
      results = results.slice(start, end)
    }
    
    return results as T[]
  }

  async getById<T>(table: string, id: string): Promise<T | null> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const result = await this.db!.get(storeName, id)
    return (result || null) as T | null
  }

  async insert<T>(table: string, data: Partial<T>): Promise<OperationResult<T>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const now = new Date().toISOString()
    
    logger.info(`[StandaloneProvider] Insert called on ${table}`)
    logger.info(`[StandaloneProvider] Input data type:`, typeof data)
    logger.info(`[StandaloneProvider] Input data keys:`, Object.keys(data as object))
    
    // Check for proxy objects in the data
    for (const [key, value] of Object.entries(data as object)) {
      if (value && typeof value === 'object') {
        const proto = Object.prototype.toString.call(value)
        logger.info(`[StandaloneProvider] Field ${key}: type=${proto}, isProxy=${value.constructor?.name}`)
      }
    }
    
    // Deep clone to ensure no proxies - IndexedDB can't handle Vue proxies
    let cleanData: any
    try {
      cleanData = JSON.parse(JSON.stringify(data))
      logger.info(`[StandaloneProvider] Successfully JSON-cloned data`)
    } catch (jsonError) {
      logger.error(`[StandaloneProvider] JSON clone failed:`, jsonError)
      cleanData = data
    }
    
    // Generate ID if not provided
    const record = {
      ...cleanData,
      id: cleanData.id || crypto.randomUUID(),
      created_at: cleanData.created_at || now,
      updated_at: now
    }
    
    logger.info(`[StandaloneProvider] Final record to insert:`, record)
    
    try {
      await this.db!.put(storeName, record)
      
      // Broadcast change to other tabs
      this.broadcastChange(table, {
        eventType: 'INSERT',
        table,
        old: null,
        new: record,
        errors: null
      })
      
      return { success: true, data: record as T }
    } catch (error) {
      logger.error(`[StandaloneProvider] Insert error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async insertMany<T>(table: string, data: Partial<T>[]): Promise<OperationResult<T[]>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const now = new Date().toISOString()
    const records: T[] = []
    
    try {
      const tx = this.db!.transaction(storeName, 'readwrite')
      
      for (const item of data) {
        const record = {
          ...item,
          id: (item as any).id || crypto.randomUUID(),
          created_at: (item as any).created_at || now,
          updated_at: now
        }
        await tx.store.put(record)
        records.push(record as T)
      }
      
      await tx.done
      
      // Broadcast changes
      for (const record of records) {
        this.broadcastChange(table, {
          eventType: 'INSERT',
          table,
          old: null,
          new: record,
          errors: null
        })
      }
      
      return { success: true, data: records }
    } catch (error) {
      logger.error(`[StandaloneProvider] InsertMany error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async update<T>(table: string, id: string, data: Partial<T>): Promise<OperationResult<T>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    
    try {
      const existing = await this.db!.get(storeName, id)
      if (!existing) {
        return { success: false, error: 'Record not found' }
      }
      
      const updated = {
        ...existing,
        ...data,
        id, // Ensure ID doesn't change
        updated_at: new Date().toISOString()
      }
      
      await this.db!.put(storeName, updated)
      
      // Broadcast change
      this.broadcastChange(table, {
        eventType: 'UPDATE',
        table,
        old: existing,
        new: updated,
        errors: null
      })
      
      return { success: true, data: updated as T }
    } catch (error) {
      logger.error(`[StandaloneProvider] Update error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async updateMany<T>(table: string, filters: QueryFilter[], data: Partial<T>): Promise<OperationResult<number>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    
    try {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const all = await tx.store.getAll()
      const matching = all.filter(item => this.matchesFilters(item, filters))
      
      let count = 0
      for (const item of matching) {
        const updated = {
          ...item,
          ...data,
          updated_at: new Date().toISOString()
        }
        await tx.store.put(updated)
        
        this.broadcastChange(table, {
          eventType: 'UPDATE',
          table,
          old: item,
          new: updated,
          errors: null
        })
        count++
      }
      
      await tx.done
      return { success: true, data: count }
    } catch (error) {
      logger.error(`[StandaloneProvider] UpdateMany error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async upsert<T>(table: string, data: Partial<T>, onConflict?: string): Promise<OperationResult<T>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const id = (data as any).id
    
    // If onConflict is specified, search by those fields instead of id
    if (onConflict) {
      const conflictFields = onConflict.split(',').map(f => f.trim())
      const allRecords = await this.db!.getAll(storeName)
      
      const existing = allRecords.find(record => 
        conflictFields.every(field => (record as any)[field] === (data as any)[field])
      )
      
      if (existing) {
        return this.update(table, (existing as any).id, data)
      }
    } else if (id) {
      const existing = await this.db!.get(storeName, id)
      if (existing) {
        return this.update(table, id, data)
      }
    }
    
    return this.insert(table, data)
  }

  async delete(table: string, id: string, deletionBatchId?: string): Promise<OperationResult> {
    await this.ensureInitialized()
    
    const storeName = table as any
    
    try {
      const existing = await this.db!.get(storeName, id)
      
      if (existing) {
        // Soft delete: set deleted_at timestamp and optional batch ID
        const updated: Record<string, unknown> = {
          ...existing,
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        if (deletionBatchId) {
          updated.deletion_batch_id = deletionBatchId
        }
        await this.db!.put(storeName, updated)
        
        this.broadcastChange(table, {
          eventType: 'DELETE',
          table,
          old: existing,
          new: updated,
          errors: null
        })
      }
      
      return { success: true }
    } catch (error) {
      logger.error(`[StandaloneProvider] Soft delete error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async deleteMany(table: string, filters: QueryFilter[], deletionBatchId?: string): Promise<OperationResult<number>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const now = new Date().toISOString()
    
    try {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const all = await tx.store.getAll()
      const matching = all.filter(item => this.matchesFilters(item, filters))
      
      let count = 0
      for (const item of matching) {
        // Soft delete: set deleted_at timestamp and optional batch ID
        const updated: Record<string, unknown> = {
          ...item,
          deleted_at: now,
          updated_at: now
        }
        if (deletionBatchId) {
          updated.deletion_batch_id = deletionBatchId
        }
        await tx.store.put(updated)
        
        this.broadcastChange(table, {
          eventType: 'DELETE',
          table,
          old: item,
          new: updated,
          errors: null
        })
        count++
      }
      
      await tx.done
      return { success: true, data: count }
    } catch (error) {
      logger.error(`[StandaloneProvider] Soft deleteMany error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async hardDelete(table: string, id: string): Promise<OperationResult> {
    await this.ensureInitialized()
    
    const storeName = table as any
    
    try {
      const existing = await this.db!.get(storeName, id)
      await this.db!.delete(storeName, id)
      
      if (existing) {
        this.broadcastChange(table, {
          eventType: 'DELETE',
          table,
          old: existing,
          new: null,
          errors: null
        })
      }
      
      return { success: true }
    } catch (error) {
      logger.error(`[StandaloneProvider] Hard delete error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async restore(table: string, id: string): Promise<OperationResult> {
    await this.ensureInitialized()
    
    const storeName = table as any
    
    try {
      const existing = await this.db!.get(storeName, id)
      
      if (existing) {
        // Restore: clear deleted_at and deletion_batch_id
        const updated = {
          ...existing,
          deleted_at: null,
          deletion_batch_id: null,
          updated_at: new Date().toISOString()
        }
        await this.db!.put(storeName, updated)
        
        this.broadcastChange(table, {
          eventType: 'UPDATE',
          table,
          old: existing,
          new: updated,
          errors: null
        })
      }
      
      return { success: true }
    } catch (error) {
      logger.error(`[StandaloneProvider] Restore error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async restoreMany(table: string, filters: QueryFilter[]): Promise<OperationResult<number>> {
    await this.ensureInitialized()
    
    const storeName = table as any
    const now = new Date().toISOString()
    
    try {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const all = await tx.store.getAll()
      const matching = all.filter(item => this.matchesFilters(item, filters))
      
      let count = 0
      for (const item of matching) {
        // Restore: clear deleted_at and deletion_batch_id
        const updated = {
          ...item,
          deleted_at: null,
          deletion_batch_id: null,
          updated_at: now
        }
        await tx.store.put(updated)
        
        this.broadcastChange(table, {
          eventType: 'UPDATE',
          table,
          old: item,
          new: updated,
          errors: null
        })
        count++
      }
      
      await tx.done
      return { success: true, data: count }
    } catch (error) {
      logger.error(`[StandaloneProvider] RestoreMany error on ${table}:`, error)
      return { success: false, error: String(error) }
    }
  }

  async restoreBatch(batchId: string): Promise<OperationResult<number>> {
    await this.ensureInitialized()
    
    let totalRestored = 0
    const now = new Date().toISOString()
    
    // Iterate over all object stores to find matching batch items
    const storeNames = [...this.db!.objectStoreNames]
    
    for (const storeName of storeNames) {
      try {
        const tx = this.db!.transaction(storeName as any, 'readwrite')
        const all = await tx.store.getAll()
        
        for (const item of all) {
          if ((item as any).deletion_batch_id === batchId) {
            const updated = {
              ...item,
              deleted_at: null,
              deletion_batch_id: null,
              updated_at: now
            }
            await tx.store.put(updated)
            totalRestored++
          }
        }
        
        await tx.done
      } catch {
        // Store might not exist or other issue, skip
      }
    }
    
    return { success: true, data: totalRestored }
  }

  // ==================== Realtime / Cross-tab Sync ====================

  subscribe<T>(
    table: string,
    callback: (payload: ChangePayload<T>) => void,
    filters?: QueryFilter[]
  ): Subscription {
    const channelName = `ambi-table-${table}`
    
    let channel = this.broadcastChannels.get(channelName)
    if (!channel) {
      channel = new BroadcastChannel(channelName)
      this.broadcastChannels.set(channelName, channel)
      logger.info(`[StandaloneProvider] Created BroadcastChannel: ${channelName}`)
    }
    
    const handler = (event: MessageEvent) => {
      const payload = event.data as ChangePayload<T>
      logger.info(`[StandaloneProvider] Received broadcast on ${channelName}:`, payload.eventType, payload.table)
      
      // Apply filters if provided
      if (filters && filters.length > 0) {
        const record = payload.new || payload.old
        if (record && !this.matchesFilters(record, filters)) {
          logger.debug(`[StandaloneProvider] Filtered out message on ${channelName}`)
          return
        }
      }
      
      callback(payload)
    }
    
    channel.addEventListener('message', handler)
    logger.info(`[StandaloneProvider] Subscribed to ${channelName}`)
    
    return {
      unsubscribe: () => {
        channel?.removeEventListener('message', handler)
        logger.info(`[StandaloneProvider] Unsubscribed from ${channelName}`)
      }
    }
  }

  subscribeToChannel(
    channelName: string,
    eventHandlers: Record<string, (payload: any) => void>
  ): Subscription {
    const fullChannelName = `ambi-custom-${channelName}`
    
    let channel = this.broadcastChannels.get(fullChannelName)
    if (!channel) {
      channel = new BroadcastChannel(fullChannelName)
      this.broadcastChannels.set(fullChannelName, channel)
    }
    
    const handler = (event: MessageEvent) => {
      const { eventName, payload } = event.data
      const eventHandler = eventHandlers[eventName]
      if (eventHandler) {
        eventHandler(payload)
      }
    }
    
    channel.addEventListener('message', handler)
    
    return {
      unsubscribe: () => {
        channel?.removeEventListener('message', handler)
      }
    }
  }

  async broadcast(channel: string, event: string, payload: any): Promise<void> {
    const fullChannelName = `ambi-custom-${channel}`
    
    let broadcastChannel = this.broadcastChannels.get(fullChannelName)
    if (!broadcastChannel) {
      broadcastChannel = new BroadcastChannel(fullChannelName)
      this.broadcastChannels.set(fullChannelName, broadcastChannel)
    }
    
    broadcastChannel.postMessage({ eventName: event, payload })
  }

  // ==================== File Storage ====================

  async uploadFile(bucket: string, path: string, file: Blob | File): Promise<OperationResult<string>> {
    await this.ensureInitialized()
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return { success: false, error: `Unknown bucket: ${bucket}` }
    }
    
    try {
      const metadata: FileMetadata = {
        path: path,
        size: file.size,
        contentType: file instanceof File ? file.type : 'application/octet-stream',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      // Store using the path as-is (caller includes userId in path)
      await this.db!.put(storeName as any, { blob: file, metadata }, path)
      
      return { success: true, data: path }
    } catch (error) {
      logger.error(`[StandaloneProvider] Upload error:`, error)
      return { success: false, error: String(error) }
    }
  }

  async getFileUrl(bucket: string, path: string, _expiresIn?: number): Promise<string | null> {
    await this.ensureInitialized()
    
    // Check cache first
    const cacheKey = `${bucket}/${path}`
    const cached = this.objectUrlCache.get(cacheKey)
    if (cached) {
      return cached
    }
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return null
    }
    
    try {
      const result = await this.db!.get(storeName as any, path)
      if (!result) {
        return null
      }
      
      // Create object URL from blob
      const url = URL.createObjectURL(result.blob)
      this.objectUrlCache.set(cacheKey, url)
      
      return url
    } catch (error) {
      logger.error(`[StandaloneProvider] GetFileUrl error:`, error)
      return null
    }
  }

  async deleteFile(bucket: string, path: string): Promise<OperationResult> {
    await this.ensureInitialized()
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return { success: false, error: `Unknown bucket: ${bucket}` }
    }
    
    try {
      await this.db!.delete(storeName as any, path)
      
      // Revoke cached object URL
      const cacheKey = `${bucket}/${path}`
      const cached = this.objectUrlCache.get(cacheKey)
      if (cached) {
        URL.revokeObjectURL(cached)
        this.objectUrlCache.delete(cacheKey)
      }
      
      return { success: true }
    } catch (error) {
      logger.error(`[StandaloneProvider] DeleteFile error:`, error)
      return { success: false, error: String(error) }
    }
  }

  async listFiles(bucket: string, path: string): Promise<FileMetadata[]> {
    await this.ensureInitialized()
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return []
    }
    
    try {
      const tx = this.db!.transaction(storeName as any, 'readonly')
      const all = await tx.store.getAll()
      
      return all
        .filter(item => item.metadata.path.startsWith(path))
        .map(item => item.metadata)
    } catch (error) {
      logger.error(`[StandaloneProvider] ListFiles error:`, error)
      return []
    }
  }

  // ==================== Auth ====================

  getAuthState(): AuthState {
    return {
      user: this.currentUser,
      isAuthenticated: this.currentUser !== null
    }
  }

  getCurrentUserId(): string | null {
    return this.currentUser?.id || null
  }

  onAuthStateChange(callback: (state: AuthState) => void): Subscription {
    this.authStateCallbacks.add(callback)
    
    // Immediately call with current state
    callback(this.getAuthState())
    
    return {
      unsubscribe: () => {
        this.authStateCallbacks.delete(callback)
      }
    }
  }

  // ==================== Provider Info ====================

  getMode(): 'online' | 'standalone' {
    return 'standalone'
  }

  isReady(): boolean {
    return this.initialized
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.info('[StandaloneProvider] Already initialized')
      return
    }
    
    logger.info('[StandaloneProvider] Initializing...')
    
    // Open/create IndexedDB
    this.db = await openDB<AmbiDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create data stores
        const tables = Object.values(TABLES)
        for (const table of tables) {
          if (!db.objectStoreNames.contains(table as any)) {
            // Use any for store since we're creating dynamic tables
            const store = db.createObjectStore(table as any, { keyPath: 'id' }) as any
            
            // Add common indexes - user_id for most tables
            if (table !== 'display_layers' && table !== 'display_layer_overrides') {
              store.createIndex('by-user', 'user_id')
            }
            
            // Add specific indexes based on table type
            if (table === 'display_layers' || table === 'display_layer_overrides') {
              store.createIndex('by-config', 'scene_display_config_id')
            } else if (table === 'scene_display_configs') {
              store.createIndex('by-scene', 'scene_id')
              store.createIndex('by-display', 'display_id')
            } else if (table === 'image_library' || table === 'audio_library' || table === 'audio_library_files') {
              store.createIndex('by-folder', 'folder_id')
            } else if (table === 'scene_music_configs' || table === 'scene_spotify_configs' || table === 'scene_playlist_configs') {
              // These scene config tables need by-scene index
              store.createIndex('by-scene', 'scene_id')
            } else if (table === 'scene_light_configs') {
              // Scene light configs need both by-scene and by-light indexes
              store.createIndex('by-scene', 'scene_id')
              store.createIndex('by-light', 'light_id')
            } else if (table === 'lights') {
              store.createIndex('by-bridge', 'bridge_id')
            } else if (table === 'wled_devices') {
              store.createIndex('by-mac', 'mac_address')
            } else if (table === 'wled_outputs') {
              store.createIndex('by-device', 'wled_device_id')
            } else if (table === 'game_rooms') {
              store.createIndex('by-gm', 'gm_user_id')
              store.createIndex('by-code', 'room_code')
            } else if (table === 'characters') {
              store.createIndex('by-room', 'room_id')
            }
          }
        }
        
        // Create storage stores
        const buckets = Object.values(STORAGE_BUCKETS)
        for (const bucket of buckets) {
          const storeName = `storage-${bucket}` as any
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName)
          }
        }
      }
    })
    
    // Load or create standalone user
    await this.loadOrCreateUser()
    
    this.initialized = true
    logger.info('[StandaloneProvider] Initialized successfully')
  }

  async cleanup(): Promise<void> {
    logger.info('[StandaloneProvider] Cleaning up...')
    
    // Close all broadcast channels
    for (const [name, channel] of this.broadcastChannels) {
      channel.close()
      logger.debug(`[StandaloneProvider] Closed broadcast channel: ${name}`)
    }
    this.broadcastChannels.clear()
    
    // Revoke all object URLs
    for (const url of this.objectUrlCache.values()) {
      URL.revokeObjectURL(url)
    }
    this.objectUrlCache.clear()
    
    // Close database
    this.db?.close()
    this.db = null
    
    this.initialized = false
    logger.info('[StandaloneProvider] Cleanup complete')
  }

  // ==================== Standalone-specific Methods ====================

  /**
   * Get a file blob directly from IndexedDB storage
   * Used by migration service for export
   */
  async getFileBlob(bucket: string, path: string): Promise<Blob | null> {
    await this.ensureInitialized()
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return null
    }
    
    try {
      const result = await this.db!.get(storeName as any, path)
      return result?.blob || null
    } catch (error) {
      logger.error(`[StandaloneProvider] GetFileBlob error:`, error)
      return null
    }
  }

  /**
   * Get all file paths in a storage bucket
   * Used by migration service for export
   */
  async getAllFilePaths(bucket: string): Promise<string[]> {
    await this.ensureInitialized()
    
    const storeName = this.getBucketStoreName(bucket)
    if (!storeName) {
      return []
    }
    
    try {
      const tx = this.db!.transaction(storeName as any, 'readonly')
      const keys = await tx.store.getAllKeys()
      return keys as string[]
    } catch (error) {
      logger.error(`[StandaloneProvider] GetAllFilePaths error:`, error)
      return []
    }
  }

  /**
   * Export all data as a JSON blob for backup (legacy - use migrationService for ZIP export)
   * @deprecated Use exportToZip from migrationService instead
   */
  async exportData(): Promise<Blob> {
    await this.ensureInitialized()
    
    const exportData: Record<string, any[]> = {}
    
    for (const table of Object.values(TABLES)) {
      const data = await this.query(table)
      exportData[table] = data
    }
    
    // Note: Files are not included in JSON export - use migrationService.exportToZip for full export
    
    return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
  }

  /**
   * Import data from a JSON backup (legacy - use migrationService for ZIP import)
   * @deprecated Use importFromZip from migrationService instead
   */
  async importData(data: Blob): Promise<OperationResult> {
    await this.ensureInitialized()
    
    try {
      const text = await data.text()
      const importData = JSON.parse(text) as Record<string, any[]>
      
      for (const [table, records] of Object.entries(importData)) {
        if (Object.values(TABLES).includes(table as any)) {
          for (const record of records) {
            await this.upsert(table, record)
          }
        }
      }
      
      return { success: true }
    } catch (error) {
      logger.error('[StandaloneProvider] Import error:', error)
      return { success: false, error: String(error) }
    }
  }

  // ==================== Private Helpers ====================

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize()
    }
  }

  private async loadOrCreateUser(): Promise<void> {
    const stored = localStorage.getItem(STANDALONE_USER_KEY)
    
    if (stored) {
      this.currentUser = JSON.parse(stored)
      logger.info('[StandaloneProvider] Loaded existing standalone user:', this.currentUser?.id)
    } else {
      // Create new standalone user
      this.currentUser = {
        id: crypto.randomUUID(),
        email: null,
        username: 'Standalone User',
        createdAt: new Date().toISOString()
      }
      localStorage.setItem(STANDALONE_USER_KEY, JSON.stringify(this.currentUser))
      logger.info('[StandaloneProvider] Created new standalone user:', this.currentUser.id)
    }
    
    // Notify auth state listeners
    const state = this.getAuthState()
    for (const callback of this.authStateCallbacks) {
      callback(state)
    }
  }

  private matchesFilters(item: any, filters: QueryFilter[]): boolean {
    for (const filter of filters) {
      const value = item[filter.column]
      
      switch (filter.operator) {
        case 'eq':
          if (value !== filter.value) return false
          break
        case 'neq':
          if (value === filter.value) return false
          break
        case 'gt':
          if (!(value > filter.value)) return false
          break
        case 'gte':
          if (!(value >= filter.value)) return false
          break
        case 'lt':
          if (!(value < filter.value)) return false
          break
        case 'lte':
          if (!(value <= filter.value)) return false
          break
        case 'like':
          if (!String(value).includes(filter.value)) return false
          break
        case 'ilike':
          if (!String(value).toLowerCase().includes(String(filter.value).toLowerCase())) return false
          break
        case 'in':
          if (!Array.isArray(filter.value) || !filter.value.includes(value)) return false
          break
        case 'is':
          if (filter.value === null && value !== null) return false
          if (filter.value !== null && value === null) return false
          break
        case 'contains':
          if (!Array.isArray(value) || !value.includes(filter.value)) return false
          break
      }
    }
    
    return true
  }

  private compareValues(a: any, b: any): number {
    if (a === b) return 0
    if (a === null || a === undefined) return -1
    if (b === null || b === undefined) return 1
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b)
    }
    return a < b ? -1 : 1
  }

  private broadcastChange<T>(table: string, payload: ChangePayload<T>): void {
    const channelName = `ambi-table-${table}`
    
    let channel = this.broadcastChannels.get(channelName)
    if (!channel) {
      channel = new BroadcastChannel(channelName)
      this.broadcastChannels.set(channelName, channel)
      logger.info(`[StandaloneProvider] Created BroadcastChannel for broadcast: ${channelName}`)
    }
    
    logger.info(`[StandaloneProvider] Broadcasting on ${channelName}:`, payload.eventType, payload.table)
    channel.postMessage(payload)
  }

  private getBucketStoreName(bucket: string): string | null {
    const bucketMap: Record<string, string> = {
      [STORAGE_BUCKETS.USER_IMAGES]: 'storage-user-images',
      [STORAGE_BUCKETS.USER_AUDIO]: 'storage-user-audio',
      [STORAGE_BUCKETS.AUDIO_FILES]: 'storage-audio-files'
    }
    return bucketMap[bucket] || null
  }
}
